// 채팅 채널(설계서 §9.6) — 에이전트가 만든 "chat" DataChannel 을 감싼다.
//
// 규약은 `protocol.ts`(에이전트 `chat.rs` 와 같은 표). 이 파일은 채널 수명·조립·콜백만 맡는다.
// 🔴 이 채널은 **보기 전용 세션에도 온다** — 채팅은 원격 기기를 조작하는 것이 아니라 사람에게 말을 거는 일이다.

import {
  CHAT_ERR,
  CHAT_ERR_MESSAGE,
  ChatError,
  Incoming,
  encodeAck,
  encodeCancel,
  encodeImageFrames,
  encodeText,
  parse,
  type ChatCaps,
} from './protocol';

/** 화면에 올릴 대화 한 건. */
export interface ChatEntry {
  /** 규약 전송 id(같은 글의 중복 표시를 막는 기준). */
  id: string;
  /** 보낸 쪽 — 'me' = 이 뷰어, 'them' = 상대(피제어). */
  from: 'me' | 'them';
  /** 내용. 이미지는 화면에 그릴 수 있는 blob: URL 이다. */
  kind: 'text' | 'image';
  text?: string;
  imageUrl?: string;
  /** 받은/보낸 시각(표시·내려받기용). */
  at: number;
  /** 보낸 글이 상대 화면에 놓였는가(내가 보낸 것에만 의미가 있다). undefined = 아직 답이 없다. */
  delivered?: boolean;
  /** 실패했으면 사용자 문구. */
  error?: string;
}

/** 보내기 대기열이 이 이상이면 비워질 때까지 기다린다(이미지를 한꺼번에 밀어 넣지 않는다). */
const SEND_HIGH_WATER = 1024 * 1024;
const SEND_POLL_MS = 5;

/**
 * 화면(훅)이 대화를 받는 통로.
 *
 * 🔴 왜 생성자 콜백과 따로 있는가: 채널은 세션이 열릴 때 **세션 매니저**가 만드는데, 대화를 그려야 하는
 * 화면(`useChat`)은 세션 객체만 받고 매니저 콜백에는 닿지 못한다. 그래서 화면이 여기에 직접 리스너를 단다.
 */
export interface ChatListener {
  onEntry(entry: ChatEntry): void;
  onAck(id: string, ok: boolean, message?: string): void;
}

export class ChatChannel {
  private readonly dc: RTCDataChannel;
  private caps: ChatCaps | null = null;
  /** 진행 중인 이미지 수신(상대가 보내는 중). 한 번에 한 건 — 에이전트도 같은 규칙이다. */
  private incoming: Incoming | null = null;
  private nextId = 1;
  private listener: ChatListener | null = null;
  /**
   * 리스너가 달리기 **전에** 도착한 대화·결과.
   *
   * 채널 hello 는 영상보다 먼저 올 수 있고 화면은 영상이 온 뒤에 마운트된다 — 그 틈에 온 글을
   * 흘려버리면 "보냈는데 안 보인다" 가 된다(에이전트·앱 쪽에서 같은 이유로 큐를 둔 것과 같은 문제).
   */
  private pending: Array<() => void> = [];

  constructor(
    dc: RTCDataChannel,
    private readonly onReady: (caps: ChatCaps) => void,
  ) {
    this.dc = dc;
    dc.onmessage = (ev) => this.handle(ev.data);
  }

  /** 화면이 붙는다(달리기 전에 쌓인 것을 즉시 흘려 준다). `null` 로 떼면 다시 쌓기 시작한다. */
  setListener(l: ChatListener | null): void {
    this.listener = l;
    if (l && this.pending.length > 0) {
      const queued = this.pending;
      this.pending = [];
      for (const play of queued) {
        play();
      }
    }
  }

  private onEntry(entry: ChatEntry): void {
    const l = this.listener;
    if (l) {
      l.onEntry(entry);
    } else {
      this.pending.push(() => this.listener?.onEntry(entry));
    }
  }

  private onAck(id: string, ok: boolean, message?: string): void {
    const l = this.listener;
    if (l) {
      l.onAck(id, ok, message);
    } else {
      this.pending.push(() => this.listener?.onAck(id, ok, message));
    }
  }

  private handle(data: unknown): void {
    if (typeof data !== 'string') {
      return;
    }
    let msg;
    try {
      msg = parse(data);
    } catch {
      return; // 규약 위반은 조용히 버린다(상대가 구버전일 수 있다).
    }
    switch (msg.t) {
      case 'hello':
        this.caps = msg.caps;
        this.onReady(msg.caps);
        return;
      case 'msg':
        this.onEntry({ id: msg.id, from: 'them', kind: 'text', text: msg.text, at: Date.now() });
        // 상대에게 "화면에 놓였다" 를 알린다(뷰어는 늘 표시할 수 있다).
        this.send(encodeAck(msg.id));
        return;
      case 'put':
        // 앞의 수신이 끝나지 않았으면 거절한다(에이전트와 같은 규칙).
        if (this.incoming) {
          this.send(encodeAck(msg.id, CHAT_ERR.BUSY));
          return;
        }
        this.incoming = new Incoming(msg.id, msg.size);
        return;
      case 'data':
        if (!this.incoming || this.incoming.id !== msg.id) {
          this.send(encodeAck(msg.id, CHAT_ERR.PROTOCOL));
          return;
        }
        try {
          this.incoming.push(msg.seq, msg.d);
        } catch (e) {
          this.incoming = null; // 깨진 전송은 버린다.
          this.send(encodeAck(msg.id, e instanceof ChatError ? e.code : CHAT_ERR.PROTOCOL));
        }
        return;
      case 'end': {
        const inc = this.incoming;
        this.incoming = null;
        if (!inc || inc.id !== msg.id) {
          this.send(encodeAck(msg.id, CHAT_ERR.PROTOCOL));
          return;
        }
        try {
          const png = inc.finish();
          // 화면에 그릴 수 있는 형태로 바꾼다. 🔴 URL 해제는 화면 쪽 책임이다
          // (여기서 지우면 아직 그리는 중인 <img> 가 깨진다).
          const url = URL.createObjectURL(new Blob([png as unknown as BlobPart], { type: 'image/png' }));
          this.onEntry({ id: msg.id, from: 'them', kind: 'image', imageUrl: url, at: Date.now() });
          this.send(encodeAck(msg.id));
        } catch (e) {
          this.send(encodeAck(msg.id, e instanceof ChatError ? e.code : CHAT_ERR.PROTOCOL));
        }
        return;
      }
      case 'cancel':
        if (this.incoming?.id === msg.id) {
          this.incoming = null;
        }
        return;
      case 'ack':
        this.onAck(
          msg.id,
          msg.ok,
          msg.ok ? undefined : (CHAT_ERR_MESSAGE[msg.err ?? ''] ?? '전달하지 못했습니다.'),
        );
        return;
    }
  }

  private send(frame: string): void {
    if (this.dc.readyState === 'open') {
      this.dc.send(frame);
    }
  }

  /** 현재 능력(없으면 아직 hello 전). */
  get capabilities(): ChatCaps | null {
    return this.caps;
  }

  get ready(): boolean {
    return this.caps !== null && this.dc.readyState === 'open';
  }

  /** 상대가 이미지를 받을 수 있는가(hello.kinds) — 뷰어가 붙여넣기를 막는 근거. */
  get canSendImage(): boolean {
    return this.caps?.kinds.includes('image') ?? false;
  }

  private makeId(): string {
    return `v${this.nextId++}`;
  }

  /**
   * 글을 보낸다. 돌려주는 id 로 나중에 `onAck` 결과를 짝짓는다.
   * 상한 검사는 호출부(훅)가 미리 한다 — 사용자에게 **보내기 전에** 알려야 하기 때문이다.
   */
  sendText(text: string): string {
    const id = this.makeId();
    this.send(encodeText(id, text));
    return id;
  }

  /**
   * 이미지를 보낸다(조각 분할 + 흐름 제어). 큰 이미지는 여러 조각으로 나가므로 비동기다.
   * 🔴 대기열을 보며 보낸다 — 수 MB 를 한꺼번에 밀어 넣으면 같은 연결의 다른 채널까지 밀린다.
   */
  async sendImage(png: Uint8Array): Promise<string> {
    const id = this.makeId();
    for (const frame of encodeImageFrames(id, png)) {
      while (this.dc.readyState === 'open' && this.dc.bufferedAmount > SEND_HIGH_WATER) {
        await new Promise((r) => setTimeout(r, SEND_POLL_MS));
      }
      if (this.dc.readyState !== 'open') {
        break; // 세션이 끊겼다 — 훅이 ack 없음으로 처리한다.
      }
      this.dc.send(frame);
    }
    return id;
  }

  cancel(id: string): void {
    this.send(encodeCancel(id));
  }
}
